// In-memory state management for garden objects (ground shapes, buildings, plants)
// Follows the ZoneState pattern with dirty tracking for save

import type { GardenObject, Plant, Building, GroundShape, Layer } from '@/types/garden'
import { debug } from '@/utils/debug'

export type DirtyStatus = 'new' | 'modified' | 'deleted'

export interface DirtyObject {
  status: DirtyStatus
  object: GardenObject
  /** Server-assigned ID (for existing objects) */
  serverId?: string
}

export interface StateChange {
  type: 'add' | 'update' | 'delete' | 'restore' | 'bulk'
  objectId?: string
  /** Which properties changed (for 'update' type) */
  updatedKeys?: string[]
}

export type GardenObjectStateChangeCallback = (change: StateChange) => void

let nextLocalId = 1

function generateLocalId(): string {
  return `local_${nextLocalId++}`
}

/**
 * Garden object state manager — manages garden objects in memory
 * with dirty tracking for the save service
 */
class GardenObjectStateManager {
  private objects: Map<string, GardenObject> = new Map()
  /** Maps local IDs to server IDs for existing objects */
  private localToServerId: Map<string, string> = new Map()
  /** Tracks dirty state for save */
  private dirtyObjects: Map<string, DirtyStatus> = new Map()
  private changeCallbacks: GardenObjectStateChangeCallback[] = []

  constructor() {
    debug.log('ObjectState', 'GardenObjectStateManager initialized')
  }

  // ============================================================================
  // CRUD operations
  // ============================================================================

  /**
   * Add a new garden object (user-created, marked as 'new' for save)
   * x,y = CENTER (same convention as ZoneState), points = CENTER-RELATIVE
   */
  addObject(
    type: GardenObject['type'],
    productId: string,
    x: number,
    y: number,
    width: number,
    height: number,
    options?: Partial<Omit<GardenObject, 'id' | 'type' | 'productId' | 'x' | 'y' | 'width' | 'height'>>
  ): string {
    const id = generateLocalId()

    const obj: GardenObject = {
      id,
      type,
      productId,
      x,
      y,
      z: options?.z ?? 0,
      width,
      height,
      rotation: options?.rotation ?? 0,
      elevation: options?.elevation ?? 0,
      zIndex: options?.zIndex ?? 0,
      productName: options?.productName,
      label: options?.label,
      textureUrl: options?.textureUrl,
      points: options?.points,
      pathWaypoints: options?.pathWaypoints,
      pathWidth: options?.pathWidth,
    }

    // Auto-compute width/height from center-relative polygon points (like ZoneState)
    if (obj.points && obj.points.length >= 3) {
      const xs = obj.points.map(p => p.x)
      const ys = obj.points.map(p => p.y)
      obj.width = Math.max(...xs) - Math.min(...xs)
      obj.height = Math.max(...ys) - Math.min(...ys)
    }

    this.objects.set(id, obj)
    this.dirtyObjects.set(id, 'new')

    debug.log('ObjectState', `Added ${type}: ${id} (${productId}) at (${x}, ${y})`)
    this.notifyChange({ type: 'add', objectId: id })
    return id
  }

  /**
   * Update an existing object
   */
  updateObject(id: string, updates: Partial<Omit<GardenObject, 'id' | 'type'>>): void {
    const obj = this.objects.get(id)
    if (!obj) {
      debug.warn('ObjectState', `Object not found: ${id}`)
      return
    }

    const updated = { ...obj, ...updates }

    // Auto-compute width/height from center-relative polygon points (like ZoneState)
    if (updated.points && updated.points.length >= 3) {
      const xs = updated.points.map(p => p.x)
      const ys = updated.points.map(p => p.y)
      updated.width = Math.max(...xs) - Math.min(...xs)
      updated.height = Math.max(...ys) - Math.min(...ys)
    }

    this.objects.set(id, updated)

    // Mark as modified if not already new
    if (!this.dirtyObjects.has(id) || this.dirtyObjects.get(id) !== 'new') {
      this.dirtyObjects.set(id, 'modified')
    }

    debug.log('ObjectState', `Updated ${id}`, updates)
    this.notifyChange({ type: 'update', objectId: id, updatedKeys: Object.keys(updates) })
  }

  /**
   * Delete an object
   */
  deleteObject(id: string): boolean {
    const obj = this.objects.get(id)
    if (!obj) {
      debug.warn('ObjectState', `Object not found for deletion: ${id}`)
      return false
    }

    this.objects.delete(id)

    // If it was new (never saved), just remove dirty entry
    if (this.dirtyObjects.get(id) === 'new') {
      this.dirtyObjects.delete(id)
    } else {
      // Mark for server-side deletion
      this.dirtyObjects.set(id, 'deleted')
    }

    debug.log('ObjectState', `Deleted: ${id}`)
    this.notifyChange({ type: 'delete', objectId: id })
    return true
  }

  /**
   * Restore an object with its original ID (used by undo)
   */
  restoreObject(obj: GardenObject): void {
    this.objects.set(obj.id, { ...obj })

    // Ensure nextLocalId stays ahead
    const idNum = parseInt(obj.id.split('_')[1] || '0', 10)
    if (idNum >= nextLocalId) {
      nextLocalId = idNum + 1
    }

    // Re-mark as new if it has a local ID (no server ID)
    if (!this.localToServerId.has(obj.id)) {
      this.dirtyObjects.set(obj.id, 'new')
    } else {
      this.dirtyObjects.set(obj.id, 'modified')
    }

    debug.log('ObjectState', `Restored: ${obj.id}`)
    this.notifyChange({ type: 'restore', objectId: obj.id })
  }

  // ============================================================================
  // Queries
  // ============================================================================

  getObjectById(id: string): GardenObject | undefined {
    return this.objects.get(id)
  }

  getObjects(): GardenObject[] {
    return Array.from(this.objects.values())
  }

  getObjectsByLayer(layer: Layer, categoryLayerMap?: Map<string, Layer>): GardenObject[] {
    return this.getObjects().filter(obj => {
      if (layer === 'ground') {
        return ['rectangle', 'polygon', 'ellipse', 'path'].includes(obj.type)
      }
      if (layer === 'architecture') {
        return obj.type === 'building'
      }
      if (layer === 'plants') {
        return obj.type === 'plant'
      }
      // Fallback: use category map if available
      if (categoryLayerMap && obj.mainCategoryId) {
        return categoryLayerMap.get(obj.mainCategoryId) === layer
      }
      return false
    })
  }

  getPlants(): Plant[] {
    return this.getObjects().filter(o => o.type === 'plant') as Plant[]
  }

  getBuildings(): Building[] {
    return this.getObjects().filter(o => o.type === 'building') as Building[]
  }

  getGroundShapes(): GroundShape[] {
    return this.getObjects().filter(o =>
      ['rectangle', 'polygon', 'ellipse', 'path'].includes(o.type)
    ) as GroundShape[]
  }

  // ============================================================================
  // Dirty tracking (for save)
  // ============================================================================

  isDirty(): boolean {
    return this.dirtyObjects.size > 0
  }

  getDirtyObjects(): DirtyObject[] {
    const result: DirtyObject[] = []
    for (const [id, status] of this.dirtyObjects) {
      if (status === 'deleted') {
        // Object was deleted — need serverId to send DELETE
        const serverId = this.localToServerId.get(id)
        if (serverId) {
          result.push({
            status: 'deleted',
            object: { id, type: 'plant', x: 0, y: 0, z: 0, width: 0, height: 0, rotation: 0, elevation: 0, zIndex: 0 },
            serverId,
          })
        }
      } else {
        const obj = this.objects.get(id)
        if (obj) {
          result.push({
            status,
            object: obj,
            serverId: this.localToServerId.get(id),
          })
        }
      }
    }
    return result
  }

  /**
   * Mark all objects as clean (after successful save)
   */
  markAllClean(): void {
    this.dirtyObjects.clear()
    debug.log('ObjectState', 'All objects marked clean')
    this.notifyChange({ type: 'bulk' })
  }

  /**
   * Map a local ID to a server ID (after save creates the object)
   */
  setServerId(localId: string, serverId: string): void {
    this.localToServerId.set(localId, serverId)
    debug.log('ObjectState', `Mapped ${localId} → server ${serverId}`)
  }

  /**
   * Get server ID for a local ID
   */
  getServerId(localId: string): string | undefined {
    return this.localToServerId.get(localId)
  }

  // ============================================================================
  // Bulk operations
  // ============================================================================

  /**
   * Import objects from API response (initial load)
   * Server IDs are mapped, all objects start as clean.
   *
   * Ground shapes are converted from API convention (x,y = top-left,
   * points = top-left-relative) to internal convention (x,y = center,
   * points = center-relative) — matching the ZoneState pattern.
   */
  importFromGarden(
    plants: Plant[],
    buildings: Building[],
    groundShapes: GroundShape[]
  ): void {
    this.objects.clear()
    this.localToServerId.clear()
    this.dirtyObjects.clear()

    // Plants and buildings: convert API coords (top-left) → internal coords (center)
    for (const obj of [...plants, ...buildings] as GardenObject[]) {
      const localId = generateLocalId()
      const localObj: GardenObject = {
        ...obj,
        id: localId,
        x: obj.x + obj.width / 2,
        y: obj.y + obj.height / 2,
      }
      this.objects.set(localId, localObj)
      this.localToServerId.set(localId, obj.id)
    }

    // Ground shapes: convert API coords (top-left) → internal coords (center)
    for (const shape of groundShapes) {
      const localId = generateLocalId()
      const localObj: GardenObject = {
        ...shape,
        id: localId,
        // Convert top-left → center
        x: shape.x + shape.width / 2,
        y: shape.y + shape.height / 2,
      }

      // Convert points from top-left-relative → center-relative
      if (localObj.points && localObj.points.length >= 3) {
        const hw = shape.width / 2
        const hh = shape.height / 2
        localObj.points = localObj.points.map(p => ({ x: p.x - hw, y: p.y - hh }))
      }

      // Convert pathWaypoints from top-left-relative → center-relative
      if (localObj.pathWaypoints && localObj.pathWaypoints.length >= 2) {
        const hw = shape.width / 2
        const hh = shape.height / 2
        localObj.pathWaypoints = localObj.pathWaypoints.map(p => ({ x: p.x - hw, y: p.y - hh }))
      }

      this.objects.set(localId, localObj)
      this.localToServerId.set(localId, shape.id)
    }

    debug.log('ObjectState', `Imported ${plants.length + buildings.length + groundShapes.length} objects (${plants.length} plants, ${buildings.length} buildings, ${groundShapes.length} ground)`)
    // Don't call notifyChange() — renderGarden() handles initial render.
    // This avoids a double-render and ensures meshes are created with local IDs
    // when renderGarden uses gardenObjectState data.
  }

  /**
   * Clear all objects
   */
  clearAll(): void {
    this.objects.clear()
    this.localToServerId.clear()
    this.dirtyObjects.clear()
    debug.log('ObjectState', 'Cleared all objects')
    this.notifyChange({ type: 'bulk' })
  }

  // ============================================================================
  // Change tracking
  // ============================================================================

  onChange(callback: GardenObjectStateChangeCallback): () => void {
    this.changeCallbacks.push(callback)
    return () => {
      const index = this.changeCallbacks.indexOf(callback)
      if (index >= 0) {
        this.changeCallbacks.splice(index, 1)
      }
    }
  }

  private notifyChange(change: StateChange): void {
    for (const callback of this.changeCallbacks) {
      try {
        callback(change)
      } catch (error) {
        debug.error('ObjectState', 'Error in change callback', error)
      }
    }
  }
}

// Singleton instance
export const gardenObjectState = new GardenObjectStateManager()
